// src/components/businesses/BusinessSelector.tsx
// Dropdown que expone el negocio activo — se monta en toolbars donde
// las operaciones dependen de un Business (Publicar, Calendario,
// Aprobar drafts). Lee/escribe via BusinessContext.

import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle, Building2, Check, ChevronDown, Facebook, Instagram, Plus,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { useActiveBusiness } from '@/context/BusinessContext';

interface Props {
  /** If true, renders a compact inline badge instead of the full dropdown. */
  compact?: boolean;
  /** Shown in parens when no business is selected yet. */
  placeholder?: string;
}

export function BusinessSelector({ compact = false, placeholder = 'Elige un negocio' }: Props) {
  const { businesses, loading, active, setActive } = useActiveBusiness();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (loading) {
    return (
      <div className={`inline-flex items-center gap-2 text-xs text-white/40 ${compact ? '' : 'px-3 py-1.5'}`}>
        <Building2 size={12} /> Cargando…
      </div>
    );
  }

  if (!businesses || businesses.length === 0) {
    return (
      <Link
        to="/m/empresa/negocios"
        className={`inline-flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 ${compact ? 'px-2 py-1 text-[10px]' : 'px-3 py-1.5 text-xs'}`}
      >
        <AlertCircle size={12} /> Sin negocios — crea uno <Plus size={12} />
      </Link>
    );
  }

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 hover:bg-white/10 text-white ${compact ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'}`}
      >
        <Building2 size={12} className="text-white/50" />
        <span className="truncate max-w-[14em]">{active?.name || placeholder}</span>
        <ChevronDown size={12} className={`text-white/50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-72 rounded-lg border border-white/10 bg-[#1e1f2b] shadow-xl z-50 p-1.5 max-h-[70vh] overflow-auto">
          <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-white/40">Negocio activo</div>
          {businesses.map((b) => {
            const isActive = b.id === active?.id;
            return (
              <button
                key={b.id}
                onClick={() => { setActive(b.id); setOpen(false); }}
                className={`w-full text-left px-2 py-2 rounded-md flex items-start justify-between gap-2 transition-colors ${isActive ? 'bg-emerald-500/10' : 'hover:bg-white/5'}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-sm text-white truncate">
                    {b.logo_url
                      ? <img src={b.logo_url} alt="" className="w-4 h-4 rounded object-cover" />
                      : <Building2 size={12} className="text-white/40" />}
                    {b.name}
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-1 text-[10px] text-white/40">
                    {b.fb_page_name && <span className="inline-flex items-center gap-0.5"><Facebook size={9} /> {b.fb_page_name}</span>}
                    {b.ig_username && <span className="inline-flex items-center gap-0.5"><Instagram size={9} /> @{b.ig_username}</span>}
                  </div>
                </div>
                {isActive && <Check size={14} className="text-emerald-400 shrink-0 mt-1" />}
              </button>
            );
          })}
          <div className="border-t border-white/5 mt-1 pt-1">
            <Link
              to="/m/empresa/negocios"
              onClick={() => setOpen(false)}
              className="block px-2 py-1.5 text-[11px] text-white/50 hover:bg-white/5 rounded"
            >
              <Plus size={11} className="inline mr-1" /> Administrar negocios…
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
